import { UserAbility } from '@/constants';
import { filterByAccess } from '@/helpers/access';
import i18n from '@/plugins/i18n';
import type { DataTableHeader } from '@/types/vuetify';

/** All column variants. Duplicate `key` + different access — first match wins in `filterByAccess`. */
function createBetsPageHeaders(): DataTableHeader[] {
    const t = i18n.global.t;

    return [
    {
        title: '',
        value: 'select',
        key: 'select',
        align: 'center',
        sortable: false,
        minWidth: 20,
        width: 20,
        cellProps: { class: 'px-0' },
        headerProps: { class: 'px-0' },
        access: UserAbility.Bet.ViewBetAdminDetails,
    },
    {
        title: t('bets.date'),
        value: 'date',
        key: 'date',
        align: 'start',
        sortable: false,
        nowrap: true,
        headerProps: { 'data-tooltip': t('bets.date'), class: 'nowrap' },
        access: UserAbility.Bet.ViewColumnCommon,
        exportKey: 'created_at',
    },
    {
        title: t('bets.user'),
        value: 'user',
        key: 'user',
        align: 'start',
        sortable: false,
        nowrap: true,
        headerProps: { 'data-tooltip': t('bets.user'), class: 'nowrap' },
        access: UserAbility.Bet.ViewBetAdminDetails,
        exportKey: 'user_login',
    },
    {
        title: t('bets.customer'),
        value: 'user',
        key: 'user',
        align: 'start',
        sortable: false,
        nowrap: true,
        headerProps: { 'data-tooltip': t('bets.customer'), class: 'nowrap' },
        access: UserAbility.Bet.ViewColumnCommon,
        exportKey: 'user_login',
    },
    {
        title: t('bets.botOrder'),
        value: 'botOrder',
        key: 'botOrder',
        align: 'start',
        sortable: false,
        nowrap: true,
        headerProps: { 'data-tooltip': t('bets.botOrder'), class: 'nowrap' },
        access: UserAbility.Bet.ViewBetAdminDetails,
        exportKey: 'order_internal_id',
    },
    {
        title: t('bets.marketType'),
        value: 'marketType',
        key: 'marketType',
        align: 'center',
        sortable: false,
        nowrap: true,
        headerProps: { 'data-tooltip': t('bets.tooltips.marketTypeTooltip'), class: 'nowrap' },
        access: UserAbility.Bet.ViewColumnCommon,
        exportKey: 'is_live',
    },
    {
        title: t('bets.periodTypeShort'),
        value: 'period',
        key: 'period',
        align: 'center',
        sortable: false,
        nowrap: true,
        headerProps: { 'data-tooltip': t('bets.periodType'), class: 'nowrap' },
        access: UserAbility.Bet.ViewColumnCommon,
        exportKey: 'period_type_id',
    },
    {
        title: t('bets.book'),
        value: 'book',
        key: 'book',
        align: 'start',
        sortable: false,
        nowrap: true,
        headerProps: { 'data-tooltip': t('bets.tooltips.bookTooltip'), class: 'nowrap' },
        access: UserAbility.Bet.ViewBookmaker,
        exportKey: 'site_id',
    },
    {
        title: t('bets.account'),
        value: 'account',
        key: 'account',
        align: 'start',
        sortable: false,
        nowrap: true,
        headerProps: { 'data-tooltip': t('bets.account'), class: 'nowrap' },
        access: UserAbility.Bet.ViewFullBetInfo,
        exportKey: 'account_login',
    },
    {
        title: t('bets.game'),
        value: 'game',
        key: 'game',
        align: 'center',
        sortable: false,
        nowrap: true,
        headerProps: { 'data-tooltip': t('bets.game'), class: 'nowrap' },
        access: UserAbility.Bet.ViewColumnCommon,
        exportKey: 'custom_team_home_name',
    },
    {
        title: t('bets.bet'),
        value: 'bet',
        key: 'bet',
        align: 'start',
        sortable: false,
        nowrap: true,
        headerProps: { 'data-tooltip': t('bets.bet'), class: 'nowrap' },
        access: UserAbility.Bet.ViewColumnCommon,
        exportKey: 'selection_id',
    },
    {
        title: t('bets.coOdds'),
        value: 'coOdds',
        key: 'coOdds',
        align: 'end',
        sortable: false,
        nowrap: true,
        headerProps: { 'data-tooltip': t('bets.tooltips.coOddsTooltip'), class: 'nowrap' },
        access: UserAbility.Bet.ViewFullBetInfo,
        exportKey: 'odds_real',
    },
    {
        title: t('bets.plOdds'),
        value: 'plOdds',
        key: 'plOdds',
        align: 'end',
        sortable: false,
        nowrap: true,
        headerProps: { 'data-tooltip': t('bets.tooltips.plOddsTooltip'), class: 'nowrap' },
        access: UserAbility.Bet.ViewColumnCommon,
        exportKey: 'odds',
    },
    {
        title: t('bets.openOdds'),
        value: 'openOdds',
        key: 'openOdds',
        align: 'end',
        sortable: false,
        nowrap: true,
        headerProps: { 'data-tooltip': t('bets.tooltips.openOddsTooltip'), class: 'nowrap' },
        access: UserAbility.Bet.ViewFullBetInfo,
    },
    {
        title: t('bets.closeOdds'),
        value: 'closeOdds',
        key: 'closeOdds',
        align: 'end',
        sortable: false,
        nowrap: true,
        headerProps: { 'data-tooltip': t('bets.tooltips.closeOddsTooltip'), class: 'nowrap' },
        access: UserAbility.Bet.ViewFullBetInfo,
    },
    {
        title: t('bets.betVsOpen'),
        value: 'betVsOpen',
        key: 'betVsOpen',
        align: 'end',
        sortable: false,
        nowrap: true,
        headerProps: { 'data-tooltip': t('bets.tooltips.betVsOpenTooltip'), class: 'nowrap' },
        access: UserAbility.Bet.ViewFullBetInfo,
    },
    {
        title: t('bets.betVsClose'),
        value: 'betVsClose',
        key: 'betVsClose',
        align: 'end',
        sortable: false,
        nowrap: true,
        headerProps: { 'data-tooltip': t('bets.tooltips.betVsCloseTooltip'), class: 'nowrap' },
        access: UserAbility.Bet.ViewFullBetInfo,
    },
    {
        title: t('bets.score'),
        value: 'score',
        key: 'score',
        align: 'center',
        sortable: false,
        nowrap: true,
        headerProps: { 'data-tooltip': t('bets.tooltips.scoreTooltip'), class: 'nowrap' },
        access: UserAbility.Bet.ViewColumnCommon,
        exportKey: 'score',
    },
    {
        title: t('bets.stake'),
        value: 'stake',
        key: 'stake',
        align: 'end',
        sortable: false,
        nowrap: true,
        headerProps: { 'data-tooltip': t('bets.tooltips.stakeTooltip'), class: 'nowrap' },
        access: UserAbility.Bet.ViewColumnCommon,
        exportKey: 'stake_pc',
    },
    {
        title: t('bets.agentStake'),
        value: 'agentStake',
        key: 'agentStake',
        align: 'end',
        sortable: false,
        nowrap: true,
        headerProps: { 'data-tooltip': t('bets.tooltips.agentStakeTooltip'), class: 'nowrap' },
        access: UserAbility.Bet.ViewColumnCommon,
        exportKey: 'stake_agent_pc',
    },
    {
        title: t('bets.bookStake'),
        value: 'bookStake',
        key: 'bookStake',
        align: 'end',
        sortable: false,
        nowrap: true,
        headerProps: { 'data-tooltip': t('bets.tooltips.bookStakeTooltip'), class: 'nowrap' },
        access: UserAbility.Bet.ViewFullBetInfo,
        exportKey: 'stake_bookmaker_ac',
    },
    {
        title: t('bets.missingStake'),
        value: 'missingStake',
        key: 'missingStake',
        align: 'end',
        sortable: false,
        nowrap: true,
        headerProps: { 'data-tooltip': t('bets.tooltips.missingStakeTooltip'), class: 'nowrap' },
        access: UserAbility.Bet.ViewColumnCommon,
        exportKey: 'mis_stake',
    },
    {
        title: t('bets.pt'),
        value: 'pt',
        key: 'pt',
        align: 'end',
        sortable: false,
        nowrap: true,
        headerProps: { 'data-tooltip': t('bets.tooltips.ptTooltip') },
        access: UserAbility.Bet.ViewBetAdminDetails,
        exportKey: 'stake_position_company_pc',
    },
    {
        title: t('bets.agentPt'),
        value: 'agentPt',
        key: 'agentPt',
        align: 'end',
        sortable: false,
        nowrap: true,
        headerProps: { 'data-tooltip': t('bets.tooltips.agentPtTooltip') },
        access: UserAbility.Bet.ViewFullBetInfo,
        exportKey: 'stake_position_agent_pc',
    },
    {
        title: t('bets.topup'),
        value: 'topup',
        key: 'topup',
        align: 'end',
        sortable: false,
        nowrap: true,
        headerProps: { 'data-tooltip': t('bets.tooltips.topupTooltip') },
        access: UserAbility.Bet.ViewColumnCommon,
        exportKey: 'topup',
    },
    {
        title: t('bets.result'),
        value: 'result',
        key: 'result',
        align: 'center',
        sortable: false,
        nowrap: true,
        headerProps: { 'data-tooltip': t('bets.result'), class: 'nowrap' },
        access: UserAbility.Bet.ViewColumnCommon,
        exportKey: 'result',
    },
    {
        title: t('bets.commission'),
        value: 'commission',
        key: 'commission',
        align: 'end',
        sortable: false,
        nowrap: true,
        headerProps: { 'data-tooltip': t('bets.tooltips.commissionTooltip'), class: 'nowrap' },
        access: UserAbility.Bet.ViewFullBetInfo,
        exportKey: 'company_com',
    },
    {
        title: t('bets.payCommission'),
        value: 'payCommission',
        key: 'payCommission',
        align: 'end',
        sortable: false,
        nowrap: true,
        headerProps: { 'data-tooltip': t('bets.tooltips.payCommissionTooltip'), class: 'nowrap' },
        access: UserAbility.Bet.ViewPayCommission,
        exportKey: 'pay_com',
    },
    {
        title: t('bets.playerBalance'),
        value: 'playerBalance',
        key: 'playerBalance',
        align: 'end',
        sortable: false,
        nowrap: true,
        headerProps: { 'data-tooltip': t('bets.tooltips.playerBalanceTooltip'), class: 'nowrap' },
        access: UserAbility.Bet.ViewBetAdminDetails,
        exportKey: 'balance_pc',
    },
    {
        title: t('bets.customerBalance'),
        value: 'playerBalance',
        key: 'playerBalance',
        align: 'end',
        sortable: false,
        nowrap: true,
        headerProps: { 'data-tooltip': t('bets.tooltips.customerBalanceTooltip'), class: 'nowrap' },
        access: UserAbility.Bet.ViewColumnCommon,
        exportKey: 'balance_pc',
    },
    {
        title: t('bets.agentBalance'),
        value: 'agentBalance',
        key: 'agentBalance',
        align: 'end',
        sortable: false,
        nowrap: true,
        headerProps: { 'data-tooltip': t('bets.tooltips.agentBalanceTooltip'), class: 'nowrap' },
        access: UserAbility.Bet.ViewColumnCommon,
        exportKey: 'balance_agent_pc',
    },
    {
        title: t('bets.agentTotal'),
        value: 'agentTotal',
        key: 'agentTotal',
        align: 'end',
        sortable: false,
        nowrap: true,
        headerProps: { 'data-tooltip': t('bets.tooltips.agentTotalTooltip') },
        access: UserAbility.Bet.ViewAgentTotal,
        exportKey: 'agent_total_pc',
    },
    {
        title: t('bets.bookBalance'),
        value: 'bookBalance',
        key: 'bookBalance',
        align: 'end',
        sortable: false,
        nowrap: true,
        headerProps: { 'data-tooltip': t('bets.tooltips.bookBalanceTooltip'), class: 'nowrap' },
        access: UserAbility.Bet.ViewBetAdminDetails,
        exportKey: 'balance_bookmaker_ac',
    },
    {
        title: t('bets.companyBalance'),
        value: 'companyBalance',
        key: 'companyBalance',
        align: 'end',
        sortable: false,
        nowrap: true,
        headerProps: { 'data-tooltip': t('bets.tooltips.companyBalanceTooltip'), class: 'nowrap' },
        access: UserAbility.Bet.ViewBetAdminDetails,
        exportKey: 'balance_bookmaker_pc',
    },
    {
        title: t('bets.companyPl'),
        value: 'companyPl',
        key: 'companyPl',
        align: 'end',
        sortable: false,
        nowrap: true,
        headerProps: { 'data-tooltip': t('bets.tooltips.companyPlTooltip'), class: 'nowrap' },
        access: UserAbility.Bet.ViewBetAdminDetails,
        exportKey: 'company_pl_pc',
    },
    {
        title: '',
        value: 'actions',
        key: 'actions',
        align: 'end',
        sortable: false,
        nowrap: true,
        width: 72,
        cellProps: { class: 'pl-0 pr-1' },
        access: UserAbility.Bet.ViewColumnCommon,
    },
    ];
}

export function getTableHeaders(): DataTableHeader[] {
    return filterByAccess(createBetsPageHeaders(), false);
}

export function getExportHeaders(): Record<string, string> {
    return Object.fromEntries(
        filterByAccess(createBetsPageHeaders(), false)
            .filter((header) => header.exportKey)
            .map((header) => [header.exportKey!, String(header.title)])
    );
}
